import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useUserData } from '../contexts/UserDataContext';

const {
  FiTrendingUp,
  FiTarget,
  FiAward,
  FiCalendar,
  FiUsers,
  FiVideo,
  FiCheckCircle,
  FiEdit3,
  FiMail,
  FiFileText,
  FiBookOpen,
  FiClock
} = FiIcons;

function StatsOverview() {
  const navigate = useNavigate();
  const { getUserData } = useUserData();

  // Get real user data
  const profile = getUserData('profile') || {};
  const milestones = getUserData('milestones') || [];
  const schools = getUserData('schools') || [];
  const deadlines = getUserData('deadlines') || [];

  // Calculate real stats
  const profileCompletion = calculateProfileCompletion(profile);
  const schoolsContacted = schools.filter(s => s.lastContact).length;
  const milestonesCompleted = milestones.filter(m => m.status === 'completed').length;
  const totalMilestones = milestones.length;
  const upcomingDeadlines = deadlines.filter(d => {
    const daysUntil = Math.ceil((new Date(d.deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return daysUntil >= 0 && daysUntil <= 7;
  }).length;

  function calculateProfileCompletion(profile) {
    const fields = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'sport',
      'position',
      'graduationYear',
      'height',
      'weight',
      'gpa',
      'highSchool'
    ];
    const completed = fields.filter(field => profile[field]).length;
    return Math.round((completed / fields.length) * 100);
  }

  const stats = [
    {
      label: 'Profile Completion',
      value: `${profileCompletion}%`,
      change: profileCompletion >= 80 ? 'Complete' : 'In Progress',
      changeType: profileCompletion >= 80 ? 'positive' : 'neutral',
      icon: FiTarget
    },
    {
      label: 'Schools Contacted',
      value: schoolsContacted.toString(),
      change: schoolsContacted > 0 ? 'Active' : 'Get Started',
      changeType: schoolsContacted > 0 ? 'positive' : 'neutral',
      icon: FiUsers
    },
    {
      label: 'Milestones Completed',
      value: `${milestonesCompleted}/${totalMilestones}`,
      change: totalMilestones > 0 ? 'On Track' : 'Add Milestones',
      changeType: totalMilestones > 0 ? 'positive' : 'neutral',
      icon: FiCheckCircle
    },
    {
      label: 'Upcoming Deadlines',
      value: upcomingDeadlines.toString(),
      change: upcomingDeadlines > 0 ? 'This Week' : 'None',
      changeType: upcomingDeadlines > 0 ? 'neutral' : 'positive',
      icon: FiCalendar
    }
  ];

  // Show welcome message if user is just getting started
  const isNewUser = profileCompletion < 50 && schools.length === 0 && milestones.length === 0;

  if (isNewUser) {
    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={stat.icon} className="w-5 h-5 text-primary" />
                </div>
                <span
                  className={`text-sm font-medium ${
                    stat.changeType === 'positive'
                      ? 'text-green-600'
                      : stat.changeType === 'negative'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}
                >
                  {stat.change}
                </span>
              </div>
              <div className="text-2xl font-bold text-secondary mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Getting Started Guide */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-gradient-to-br from-primary to-blue-700 text-white rounded-xl p-8"
        >
          <h3 className="text-2xl font-space-grotesk font-bold mb-4">
            Welcome to AthletePath! 🎯
          </h3>
          <p className="text-blue-100 mb-6">
            Let's get you started on your scholarship journey. Here are the key steps to begin:
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-5">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                <SafeIcon icon={FiEdit3} className="w-5 h-5" />
              </div>
              <h4 className="font-semibold mb-2">1. Complete Your Profile</h4>
              <p className="text-sm text-blue-100 mb-3">
                Add your athletic stats, academic info, and personal details
              </p>
              <button
                onClick={() => navigate('/settings')}
                className="text-sm font-medium hover:underline"
              >
                Go to Settings →
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-5">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              </div>
              <h4 className="font-semibold mb-2">2. Set Up Your Timeline</h4>
              <p className="text-sm text-blue-100 mb-3">
                Create milestones and track your scholarship journey
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm font-medium hover:underline"
              >
                View Timeline →
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-5">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
              </div>
              <h4 className="font-semibold mb-2">3. Research Schools</h4>
              <p className="text-sm text-blue-100 mb-3">
                Find and save colleges that match your goals
              </p>
              <button
                onClick={() => navigate('/schools')}
                className="text-sm font-medium hover:underline"
              >
                Browse Schools →
              </button>
            </div>
          </div>
        </motion.div>

        {/* Quick Tips */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="text-lg font-space-grotesk font-semibold text-secondary mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiTarget} className="w-5 h-5 text-primary" />
              <span>Pro Tips</span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <SafeIcon
                  icon={FiCheckCircle}
                  className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  Start reaching out to coaches during your sophomore or junior year
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <SafeIcon
                  icon={FiCheckCircle}
                  className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  Create highlight videos that showcase your best skills and plays
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <SafeIcon
                  icon={FiCheckCircle}
                  className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  Keep your academic performance strong - GPA matters as much as athletics
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <h3 className="text-lg font-space-grotesk font-semibold text-secondary mb-4 flex items-center space-x-2">
              <SafeIcon icon={FiAward} className="w-5 h-5 text-accent" />
              <span>Next Steps</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <SafeIcon
                  icon={FiClock}
                  className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">Complete your athletic profile</p>
                  <p className="text-xs text-gray-600 mt-1">Add your stats, position, and measurements</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <SafeIcon
                  icon={FiClock}
                  className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">Upload highlight videos</p>
                  <p className="text-xs text-gray-600 mt-1">Showcase your skills to college coaches</p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <SafeIcon
                  icon={FiClock}
                  className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-gray-800">Create your target school list</p>
                  <p className="text-xs text-gray-600 mt-1">Research and save 20-30 colleges</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show regular overview for active users
  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <SafeIcon icon={stat.icon} className="w-5 h-5 text-primary" />
              </div>
              <span
                className={`text-sm font-medium ${
                  stat.changeType === 'positive'
                    ? 'text-green-600'
                    : stat.changeType === 'negative'
                    ? 'text-red-600'
                    : 'text-gray-600'
                }`}
              >
                {stat.change}
              </span>
            </div>
            <div className="text-2xl font-bold text-secondary mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-gray-600">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-gradient-to-br from-primary to-blue-700 text-white rounded-xl p-6"
      >
        <h3 className="text-lg font-space-grotesk font-semibold mb-4">
          Keep Your Momentum Going
        </h3>
        <p className="text-blue-100 mb-6">
          Stay on track with your scholarship journey by completing these key actions
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-4 py-3 rounded-lg transition-all flex items-center space-x-3"
          >
            <SafeIcon icon={FiEdit3} className="w-5 h-5" />
            <span className="text-sm font-medium">Update Profile</span>
          </button>
          <button
            onClick={() => navigate('/schools')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-4 py-3 rounded-lg transition-all flex items-center space-x-3"
          >
            <SafeIcon icon={FiMail} className="w-5 h-5" />
            <span className="text-sm font-medium">Contact Coach</span>
          </button>
          <button
            onClick={() => navigate('/content-planning')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white px-4 py-3 rounded-lg transition-all flex items-center space-x-3"
          >
            <SafeIcon icon={FiVideo} className="w-5 h-5" />
            <span className="text-sm font-medium">Upload Video</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default StatsOverview;